import {
  parseChecklistMeta,
  parseRequiredInfoValues,
} from "./checklistResolver.js";

/** @typedef {{ id: string, label: string, type: string, required?: boolean, recommended?: boolean }} RequiredInfoField */

/**
 * @typedef {Object} ProgramChecklist
 * @property {number} [version]
 * @property {string|null} checklistId
 * @property {string|null} checklistLabel
 * @property {string|null} checklistType
 * @property {string|null} source
 * @property {Array} tasks
 * @property {RequiredInfoField[]} requiredInfoFields
 */

/** Whether deduped_grants has a saved ProgramChecklist field (not legacy ToDone inference). */
export function hasPersistedProgramChecklist(programChecklistJson) {
  return !!parseProgramChecklist(programChecklistJson);
}

/** Parse ProgramChecklist JSON from deduped_grants. */
export function parseProgramChecklist(jsonString) {
  if (!jsonString || typeof jsonString !== "string" || !jsonString.trim()) {
    return null;
  }

  try {
    const parsed = JSON.parse(jsonString);
    if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
      return null;
    }

    if (!Array.isArray(parsed.tasks)) {
      return null;
    }

    return {
      version: parsed.version || 1,
      checklistId: parsed.checklistId || null,
      checklistLabel: parsed.checklistLabel || null,
      checklistType: parsed.checklistType || "grant",
      source: parsed.source || null,
      tasks: parsed.tasks,
      requiredInfoFields: Array.isArray(parsed.requiredInfoFields)
        ? parsed.requiredInfoFields
        : [],
    };
  } catch {
    return null;
  }
}

/** Strip completion state from tasks for grant-level templates. */
export function stripTasksToTemplate(tasks = []) {
  return tasks.map((task) => ({
    ...task,
    completed: false,
    subtasks: stripTasksToTemplate(task.subtasks || []),
  }));
}

/** Build grant-level ProgramChecklist JSON (template only). */
export function buildProgramChecklistJson({
  tasks = [],
  checklistId = null,
  checklistLabel = null,
  checklistType = "grant",
  source = "manual",
  requiredInfoFields = [],
}) {
  const payload = {
    version: 1,
    checklistId,
    checklistLabel,
    checklistType,
    source,
    tasks: stripTasksToTemplate(tasks),
    requiredInfoFields,
  };

  return JSON.stringify(payload, null, 2);
}

/** Read per-opportunity instance data from ToDone JSON. */
export function parseInstanceToDone(jsonString) {
  if (!jsonString || typeof jsonString !== "string" || !jsonString.trim()) {
    return {
      meta: { checklistId: null, checklistLabel: null, checklistType: null },
      tasks: null,
      requiredInfoValues: {},
    };
  }

  try {
    const parsed = JSON.parse(jsonString);
    if (Array.isArray(parsed)) {
      return {
        meta: { checklistId: null, checklistLabel: null, checklistType: null },
        tasks: parsed,
        requiredInfoValues: {},
      };
    }

    if (!parsed || typeof parsed !== "object") {
      return {
        meta: { checklistId: null, checklistLabel: null, checklistType: null },
        tasks: null,
        requiredInfoValues: {},
      };
    }

    return {
      meta: parseChecklistMeta(jsonString),
      tasks: Array.isArray(parsed.tasks) ? parsed.tasks : null,
      requiredInfoValues: parsed.requiredInfoValues || {},
    };
  } catch {
    return {
      meta: { checklistId: null, checklistLabel: null, checklistType: null },
      tasks: null,
      requiredInfoValues: {},
    };
  }
}

function indexCompletionById(tasks = [], map = new Map()) {
  for (const task of tasks) {
    if (task?.id != null) {
      map.set(String(task.id), !!task.completed);
    }
    indexCompletionById(task.subtasks || [], map);
  }
  return map;
}

/** Overlay saved completion state onto a grant template. */
export function mergeCompletionOntoTemplate(templateTasks = [], instanceTasks = []) {
  const completionById = indexCompletionById(instanceTasks);

  const apply = (tasks) =>
    tasks.map((task) => ({
      ...task,
      completed: completionById.get(String(task.id)) ?? false,
      subtasks: apply(task.subtasks || []),
    }));

  return apply(templateTasks);
}

/**
 * Merge grant ProgramChecklist template with per-opportunity instance ToDone.
 * Returns a full ToDone JSON string for the steps UI.
 */
export function mergeProgramChecklistWithInstance(
  programChecklist,
  instanceJson = ""
) {
  if (!programChecklist?.tasks?.length) {
    return instanceJson || "";
  }

  const instance = parseInstanceToDone(instanceJson);
  const tasks = mergeCompletionOntoTemplate(
    programChecklist.tasks,
    instance.tasks || []
  );

  const payload = {
    checklistId: programChecklist.checklistId || instance.meta.checklistId,
    checklistLabel:
      programChecklist.checklistLabel || instance.meta.checklistLabel,
    checklistType:
      programChecklist.checklistType || instance.meta.checklistType || "grant",
    tasks,
  };

  if (
    instance.requiredInfoValues &&
    Object.keys(instance.requiredInfoValues).length > 0
  ) {
    payload.requiredInfoValues = instance.requiredInfoValues;
  }

  return JSON.stringify(payload, null, 2);
}

/**
 * Build a per-opportunity instance JSON (progress + required info values).
 * Used when saving in global mode — template goes to the grant, instance stays local.
 */
export function buildInstanceToDoneJson(tasks, existingJson = "", options = {}) {
  const { requiredInfoValues } = options;
  const existing = parseInstanceToDone(existingJson);
  const meta = parseChecklistMeta(existingJson);

  const payload = {
    checklistId: meta.checklistId,
    checklistLabel: meta.checklistLabel,
    checklistType: meta.checklistType,
    tasks,
  };

  const infoValues =
    requiredInfoValues !== undefined
      ? requiredInfoValues
      : existing.requiredInfoValues;
  if (infoValues && Object.keys(infoValues).length > 0) {
    payload.requiredInfoValues = infoValues;
  }

  return JSON.stringify(payload, null, 2);
}

/**
 * Convert legacy grant ToDone (full JSON) into a ProgramChecklist template.
 * Strips requiredInfoValues and resets completion.
 */
export function programChecklistFromLegacyToDone(
  jsonString,
  requiredInfoFields = []
) {
  const instance = parseInstanceToDone(jsonString);
  if (!instance.tasks?.length) return null;

  return {
    version: 1,
    checklistId: instance.meta.checklistId,
    checklistLabel: instance.meta.checklistLabel,
    checklistType: instance.meta.checklistType || "grant",
    source: "legacy",
    tasks: stripTasksToTemplate(instance.tasks),
    requiredInfoFields,
  };
}

/** Resolve a grant template from ProgramChecklist field, with legacy ToDone fallback. */
export function resolveGrantProgramChecklist(
  programChecklistJson,
  legacyToDoneJson,
  requiredInfoFields = []
) {
  const direct = parseProgramChecklist(programChecklistJson);
  if (direct) return direct;

  if (legacyToDoneJson) {
    return programChecklistFromLegacyToDone(
      legacyToDoneJson,
      requiredInfoFields
    );
  }

  return null;
}

export { parseRequiredInfoValues };
